/**
 * LegalSettingsCard.jsx
 * Admin-Komponente für AGB & Datenschutz Management
 * - URLs bearbeiten
 * - Akzeptierungen zurücksetzen
 * - Benutzerdefinierte Bedingungen verwalten
 * - Vordefinierte Templates einfügen
 * - Status-Übersicht
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, 
  DialogDescription, DialogFooter 
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  FileText, Shield, RefreshCw, AlertTriangle, 
  CheckCircle, Users, ExternalLink, Save, Plus, Trash2, Edit, GripVertical,
  Stethoscope, Camera, Ban, Heart, Scale, Dumbbell, ChevronDown
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Vordefinierte Templates für häufig verwendete Bedingungen
const CONDITION_TEMPLATES = [
  {
    id: 'medical_clearance',
    title: 'Ärztliche Freigabe',
    description: 'Ich bestätige, dass ich ärztlich für das Training freigegeben bin und keine gesundheitlichen Einschränkungen habe, die ein Training verhindern würden.',
    required: true,
    icon: Stethoscope,
    category: 'Gesundheit'
  },
  {
    id: 'health_questionnaire',
    title: 'Gesundheitsfragebogen',
    description: 'Ich habe den Gesundheitsfragebogen wahrheitsgemäss ausgefüllt und werde Änderungen meines Gesundheitszustands unverzüglich mitteilen.',
    required: true,
    icon: Heart,
    category: 'Gesundheit'
  },
  {
    id: 'photo_permission',
    title: 'Foto-/Video-Nutzung',
    description: 'Ich erlaube die Nutzung von Fotos und Videos, die während des Trainings aufgenommen werden, für Werbezwecke und Social Media.',
    required: false,
    icon: Camera,
    category: 'Marketing'
  },
  {
    id: 'cancellation_rules',
    title: 'Absage-Regeln',
    description: 'Ich akzeptiere die Absagerichtlinien. Nicht rechtzeitig abgesagte Termine (mind. 24h vorher) werden in Rechnung gestellt.',
    required: true,
    icon: Ban,
    category: 'Buchung'
  },
  {
    id: 'inbody_consent',
    title: 'Körperanalyse-Einwilligung',
    description: 'Ich stimme der Durchführung von Körperanalysen (InBody/BIA) zu und erlaube die Speicherung dieser Daten in meinem Profil.',
    required: false,
    icon: Scale,
    category: 'Analyse'
  },
  {
    id: 'training_rules',
    title: 'Trainingsordnung',
    description: 'Ich habe die Trainings- und Hausordnung gelesen und akzeptiere diese vollständig.',
    required: true,
    icon: Dumbbell,
    category: 'Allgemein'
  },
  {
    id: 'liability_waiver',
    title: 'Haftungsausschluss',
    description: 'Ich nehme zur Kenntnis, dass das Training auf eigene Gefahr erfolgt. Ich hafte selbst für Schäden, die durch unsachgemässe Nutzung der Geräte entstehen.',
    required: true,
    icon: Shield,
    category: 'Rechtliches'
  },
  {
    id: 'newsletter',
    title: 'Newsletter & Updates',
    description: 'Ich möchte regelmässig per E-Mail über Neuigkeiten, Angebote und Trainingstipps informiert werden.',
    required: false,
    icon: FileText,
    category: 'Marketing'
  }
];

const LegalSettingsCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    agb_url: '',
    privacy_url: '',
    version: 1,
    last_updated: null,
    last_reset: null,
    custom_conditions: []
  });
  const [status, setStatus] = useState(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetReason, setResetReason] = useState('');
  const [resetting, setResetting] = useState(false);
  
  // Custom condition editing
  const [showConditionDialog, setShowConditionDialog] = useState(false);
  const [editingCondition, setEditingCondition] = useState(null);
  const [conditionForm, setConditionForm] = useState({
    title: '',
    description: '',
    required: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [settingsRes, statusRes] = await Promise.all([
        axios.get(`${API}/settings/legal`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/settings/legal/status`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setSettings({
        ...settingsRes.data,
        custom_conditions: settingsRes.data.custom_conditions || []
      });
      setStatus(statusRes.data);
    } catch (error) {
      console.error('Error loading legal settings:', error);
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/settings/legal`,
        {
          agb_url: settings.agb_url,
          privacy_url: settings.privacy_url,
          custom_conditions: settings.custom_conditions
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Einstellungen gespeichert');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (resetConfirm !== 'RESET_LEGAL') {
      toast.error('Bitte geben Sie "RESET_LEGAL" ein um zu bestätigen');
      return;
    }
    
    setResetting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/settings/legal/reset-acceptances`,
        {
          reset_agb: true,
          reset_privacy: true,
          reset_custom: true,
          reason: resetReason || 'AGB/Datenschutz Aktualisierung',
          confirmation: 'RESET_LEGAL'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${response.data.customers_updated} Kunden müssen erneut akzeptieren`);
      setShowResetDialog(false);
      setResetConfirm('');
      setResetReason('');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Zurücksetzen');
    } finally {
      setResetting(false);
    }
  };

  // Custom conditions handlers
  const openAddCondition = () => {
    setEditingCondition(null);
    setConditionForm({ title: '', description: '', required: true });
    setShowConditionDialog(true);
  };

  const openEditCondition = (condition) => {
    setEditingCondition(condition);
    setConditionForm({
      title: condition.title || '',
      description: condition.description || '',
      required: condition.required !== false
    });
    setShowConditionDialog(true);
  };

  // Template einfügen
  const addFromTemplate = (template) => {
    // Prüfen ob Template bereits vorhanden ist
    const existingTitles = settings.custom_conditions.map(c => c.title.toLowerCase());
    if (existingTitles.includes(template.title.toLowerCase())) {
      toast.error(`"${template.title}" ist bereits vorhanden`);
      return;
    }

    const newCondition = {
      id: `cond_${Date.now()}`,
      title: template.title,
      description: template.description,
      required: template.required
    };

    setSettings({
      ...settings,
      custom_conditions: [...settings.custom_conditions, newCondition]
    });
    toast.success(`"${template.title}" hinzugefügt`);
  };

  // Alle verfügbaren Templates auf einmal einfügen (nur die, die noch nicht vorhanden sind)
  const addAllTemplates = () => {
    const existingTitles = settings.custom_conditions.map(c => c.title.toLowerCase());
    const newConditions = [];
    
    for (const template of CONDITION_TEMPLATES) {
      if (!existingTitles.includes(template.title.toLowerCase())) {
        newConditions.push({
          id: `cond_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
          title: template.title,
          description: template.description,
          required: template.required
        });
      }
    }

    if (newConditions.length === 0) {
      toast.info('Alle Templates sind bereits vorhanden');
      return;
    }

    setSettings({
      ...settings,
      custom_conditions: [...settings.custom_conditions, ...newConditions]
    });
    toast.success(`${newConditions.length} Templates hinzugefügt`);
  };

  const handleSaveCondition = () => {
    if (!conditionForm.title.trim()) {
      toast.error('Bitte geben Sie einen Titel ein');
      return;
    }

    const newConditions = [...settings.custom_conditions];
    
    if (editingCondition) {
      // Update existing
      const idx = newConditions.findIndex(c => c.id === editingCondition.id);
      if (idx !== -1) {
        newConditions[idx] = {
          ...newConditions[idx],
          title: conditionForm.title,
          description: conditionForm.description,
          required: conditionForm.required
        };
      }
    } else {
      // Add new
      newConditions.push({
        id: `cond_${Date.now()}`,
        title: conditionForm.title,
        description: conditionForm.description,
        required: conditionForm.required
      });
    }

    setSettings({ ...settings, custom_conditions: newConditions });
    setShowConditionDialog(false);
    toast.success(editingCondition ? 'Bedingung aktualisiert' : 'Bedingung hinzugefügt');
  };

  const handleDeleteCondition = (conditionId) => {
    const newConditions = settings.custom_conditions.filter(c => c.id !== conditionId);
    setSettings({ ...settings, custom_conditions: newConditions });
    toast.success('Bedingung entfernt');
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex justify-center">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <CardTitle>AGB & Datenschutz</CardTitle>
              <CardDescription>
                Version {settings.version} • Verwalten Sie rechtliche Dokumente und Bedingungen
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Status Cards */}
          {status && (
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm font-medium text-green-600">Akzeptiert</span>
                </div>
                <p className="text-2xl font-bold text-green-700">{status.both_accepted}</p>
                <p className="text-xs text-gray-500">Kunden</p>
              </div>
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm font-medium text-yellow-600">Ausstehend</span>
                </div>
                <p className="text-2xl font-bold text-yellow-700">{status.pending_acceptance}</p>
                <p className="text-xs text-gray-500">Kunden</p>
              </div>
              <div className="p-4 bg-gray-100 border border-gray-200 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-600">Gesamt</span>
                </div>
                <p className="text-2xl font-bold text-gray-700">{status.total_customers}</p>
                <p className="text-xs text-gray-500">Kunden</p>
              </div>
            </div>
          )}

          {/* URL Settings */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="agb-url" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                AGB URL
              </Label>
              <div className="flex gap-2">
                <Input
                  id="agb-url"
                  type="url"
                  placeholder="https://ihre-website.ch/agb"
                  value={settings.agb_url || ''}
                  onChange={(e) => setSettings({ ...settings, agb_url: e.target.value })}
                />
                {settings.agb_url && (
                  <Button variant="outline" size="icon" asChild>
                    <a href={settings.agb_url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="privacy-url" className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Datenschutz URL
              </Label>
              <div className="flex gap-2">
                <Input
                  id="privacy-url"
                  type="url"
                  placeholder="https://ihre-website.ch/datenschutz"
                  value={settings.privacy_url || ''}
                  onChange={(e) => setSettings({ ...settings, privacy_url: e.target.value })}
                />
                {settings.privacy_url && (
                  <Button variant="outline" size="icon" asChild>
                    <a href={settings.privacy_url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Custom Conditions Section */}
          <div className="pt-4 border-t">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-gray-900">Benutzerdefinierte Bedingungen</h3>
                <p className="text-sm text-gray-500">
                  Zusätzliche Checkboxen, die Kunden akzeptieren müssen
                </p>
              </div>
              <div className="flex items-center gap-2">
                {/* Templates Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2">
                      <FileText className="w-4 h-4" />
                      Vorlagen
                      <ChevronDown className="w-3 h-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72">
                    <DropdownMenuLabel>Vordefinierte Bedingungen</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {CONDITION_TEMPLATES.map((template) => {
                      const Icon = template.icon;
                      const isAdded = settings.custom_conditions.some(
                        c => c.title.toLowerCase() === template.title.toLowerCase()
                      );
                      return (
                        <DropdownMenuItem
                          key={template.id}
                          onClick={() => addFromTemplate(template)}
                          disabled={isAdded}
                          className="flex items-start gap-3 py-2"
                        >
                          <Icon className={`w-4 h-4 mt-0.5 ${isAdded ? 'text-gray-300' : 'text-gray-500'}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={isAdded ? 'text-gray-400' : ''}>{template.title}</span>
                              {template.required && (
                                <span className="px-1.5 py-0.5 text-[10px] bg-red-100 text-red-600 rounded">
                                  Pflicht
                                </span>
                              )}
                              {isAdded && (
                                <CheckCircle className="w-3 h-3 text-green-500" />
                              )}
                            </div>
                            <p className="text-xs text-gray-400 truncate">{template.category}</p>
                          </div>
                        </DropdownMenuItem>
                      );
                    })}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={addAllTemplates} className="text-blue-600 font-medium">
                      <Plus className="w-4 h-4 mr-2" />
                      Alle Vorlagen hinzufügen
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                
                {/* Manual Add Button */}
                <Button variant="outline" size="sm" onClick={openAddCondition} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Eigene
                </Button>
              </div>
            </div>

            {settings.custom_conditions.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Keine benutzerdefinierten Bedingungen</p>
                <p className="text-xs text-gray-400 mt-1">
                  Nutzen Sie "Vorlagen" für vordefinierte Bedingungen oder erstellen Sie eigene
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {settings.custom_conditions.map((condition, index) => (
                  <div 
                    key={condition.id} 
                    className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="mt-1 text-gray-400">
                      <GripVertical className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900">{condition.title}</h4>
                        {condition.required ? (
                          <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">
                            Pflicht
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                            Optional
                          </span>
                        )}
                      </div>
                      {condition.description && (
                        <p className="text-sm text-gray-500 mt-1">{condition.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8"
                        onClick={() => openEditCondition(condition)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 text-red-500 hover:text-red-700"
                        onClick={() => handleDeleteCondition(condition.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t">
            <Button 
              variant="destructive" 
              onClick={() => setShowResetDialog(true)}
              className="gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Alle Akzeptierungen zurücksetzen
            </Button>
            <Button onClick={handleSave} disabled={saving} className="gap-2">
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Speichern
            </Button>
          </div>

          {/* Last Reset Info */}
          {settings.last_reset && (
            <div className="text-xs text-gray-500 text-center">
              Letztes Zurücksetzen: {new Date(settings.last_reset).toLocaleString('de-CH')}
              {status?.reset_count > 0 && ` (${status.reset_count}x insgesamt)`}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reset Confirmation Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Alle Akzeptierungen zurücksetzen
            </DialogTitle>
            <DialogDescription>
              Diese Aktion setzt die AGB-, Datenschutz- und alle benutzerdefinierten Akzeptierungen 
              <strong> aller {status?.total_customers || 0} Kunden</strong> zurück. 
              Alle Kunden müssen beim nächsten Login erneut akzeptieren.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Grund für das Zurücksetzen (optional)</Label>
              <Input
                placeholder="z.B. AGB Aktualisierung Januar 2026"
                value={resetReason}
                onChange={(e) => setResetReason(e.target.value)}
              />
            </div>
            
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700 mb-2">
                Geben Sie <strong>RESET_LEGAL</strong> ein um zu bestätigen:
              </p>
              <Input
                value={resetConfirm}
                onChange={(e) => setResetConfirm(e.target.value)}
                placeholder="RESET_LEGAL"
                className="font-mono"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReset}
              disabled={resetting || resetConfirm !== 'RESET_LEGAL'}
            >
              {resetting ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <AlertTriangle className="w-4 h-4 mr-2" />
              )}
              Zurücksetzen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Condition Dialog */}
      <Dialog open={showConditionDialog} onOpenChange={setShowConditionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCondition ? 'Bedingung bearbeiten' : 'Neue Bedingung hinzufügen'}
            </DialogTitle>
            <DialogDescription>
              Diese Checkbox wird Kunden beim Akzeptieren der AGB angezeigt.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="condition-title">Titel *</Label>
              <Input
                id="condition-title"
                placeholder="z.B. Ärztliche Freigabe"
                value={conditionForm.title}
                onChange={(e) => setConditionForm({ ...conditionForm, title: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="condition-desc">Beschreibung (optional)</Label>
              <Textarea
                id="condition-desc"
                placeholder="z.B. Ich bestätige, dass ich ärztlich für das Training freigegeben bin."
                value={conditionForm.description}
                onChange={(e) => setConditionForm({ ...conditionForm, description: e.target.value })}
                rows={3}
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label htmlFor="condition-required" className="font-medium">Pflichtfeld</Label>
                <p className="text-xs text-gray-500">Kunden müssen diese Bedingung akzeptieren</p>
              </div>
              <Switch
                id="condition-required"
                checked={conditionForm.required}
                onCheckedChange={(checked) => setConditionForm({ ...conditionForm, required: checked })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConditionDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleSaveCondition}>
              {editingCondition ? 'Speichern' : 'Hinzufügen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LegalSettingsCard;
